import React, { useEffect } from "react";
import { useLocation } from "react-router-dom";
import classNames from "classnames";
import SectionHeading from "components/SectionHeading";
import { TUTORIAL_VIDEOS } from "../tutorialVideos";
import classes from "../style.module.scss";

export default () => {
    const { hash } = useLocation();

    useEffect(() => {
        if (!hash) return undefined;

        const videoKey = hash.replace("#", "");
        const element = document.getElementById(videoKey);
        if (!element) return undefined;

        const timeoutId = window.setTimeout(() => {
            element.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 0);

        return () => window.clearTimeout(timeoutId);
    }, [hash]);

    return (
        <div>
            <SectionHeading
                title="Quick Start Guide"
                subtitle="A streamlined process for analyzing upgrade and design decision options for existing HOT2000 files."
            />
            <div className={classes.contentWrapper}>
                <div className={classes.faq}>
                    <h3>1. Import and Setup</h3>
                    <p>
                        Click <strong>Create New Model</strong> and upload your existing .h2k file. This will automatically
                        populate all of your project data.
                    </p>
                    <ul>
                        <li>
                            <strong>Verify F280 Weather Location:</strong> F280 weather conditions differ from HOT2000's.
                            Make sure this field, located in the Weather and Location Information section of the{" "}
                            <strong>General</strong> tab, reflects the desired location.
                        </li>
                    </ul>
                </div>

                <div className={classes.faq}>
                    <h3>2. Define Upgrade Pathways</h3>
                    <p>
                        Navigate directly to the <strong>Upgrades</strong> tab to investigate multiple improvement pathways:
                    </p>
                    <ul>
                        <li>
                            <strong>Add Packages:</strong> Create multiple upgrade packages to test different combinations
                            of energy conservation measures (ECMs) across house components. Create packages from scratch,
                            duplicate existing packages, or add previously saved upgrade packages.
                            <ul>
                                <li>
                                    <strong>Edit and Add Specific Upgrades:</strong> Hover over a cell in an upgrade package
                                    column and click the <strong>PLUS</strong> icon to add a new upgrade, or the{" "}
                                    <strong>PENCIL</strong> icon to edit an upgrade.
                                </li>
                            </ul>
                        </li>
                        <li>
                            <strong>Hide Rows:</strong> Use the toggle to hide house components that you aren't currently
                            upgrading, or that aren't present in your model, to keep the interface focused on your active
                            changes. Select which rows to hide with the eye open / eye closed icons.
                        </li>
                        <li>
                            <strong>Run Simulations:</strong> Test your changes directly within the{" "}
                            <strong>Upgrades</strong> tab and view results at the bottom of the page. You can check{" "}
                            <strong>Show Costs</strong> to see the financial impact alongside the energy performance data.
                        </li>
                    </ul>
                </div>

                <div className={classes.faq}>
                    <h3>3. Review and Validate</h3>
                    <p>
                        Once you're happy with your upgrade pathways, move to the <strong>Review</strong> tab to see the
                        full model output summary. This is where you verify technical details related to program
                        requirements, investigate hourly performance, and review CSA F280-12 outputs.
                    </p>
                </div>

                <div className={classes.faq}>
                    <h3>4. Export and Submission</h3>
                    <p>Finalize your project by generating the necessary files:</p>
                    <ul>
                        <li>
                            <strong>For Clients:</strong> Export a PDF report from the <strong>Upgrades</strong> tab to show
                            the impact of different pathways.
                        </li>
                        <li>
                            <strong>When Finished:</strong> Use the <strong>Review</strong> tab to generate your submission
                            files. Click <strong>Save &amp; Export</strong> to generate your final .h2k file, and click{" "}
                            <strong>Export Data Collection Form</strong> for your DCF required for program submission.
                        </li>
                    </ul>
                    <p>
                        <strong>Tip:</strong> After saving and exporting, the app scans for potential errors that might
                        prevent HOT2000 from opening the file. If you receive a notification, check the flagged fields to
                        ensure your data was captured correctly.
                    </p>
                </div>

                <div className={classes.faqSection}>
                    <h2>Video Tutorials</h2>
                    {TUTORIAL_VIDEOS.map(({ key, title, youtubeId }) => (
                        <div key={key} id={key} className={classNames(classes.faq, classes.videoSection)}>
                            <h3>{title}</h3>
                            <div className={classes.videoEmbed}>
                                <iframe
                                    title={title}
                                    src={`https://www.youtube.com/embed/${youtubeId}`}
                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                    allowFullScreen
                                />
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};
